import React, { KeyboardEvent, useEffect } from 'react';
import { BodyText, Button, Heading, Link, SignInGoogleButton } from '@placer-ui/components';
import { InputFieldController } from 'features/authentication/signup/components/input-field-controller/input-field-controller';
import { useForm } from 'react-hook-form';
import { emailRules } from 'features/authentication/signup/validations/validations';
import { registerHubspotLead } from 'features/authentication/signup/utils/signup-hubspot';
import { AuthError } from 'features/authentication/utils/auth-error';
import { SIGNUP_FULL_FORM_STEP_ROUTE } from 'features/authentication/signup/constants/signup-links';
import { useEncodedQuery, useQuery } from 'hooks/use-query/use-query';
import { goToSignIn } from 'router/go-to';
import { useHistory, useLocation } from 'react-router-dom';
import { SsoLink } from 'features/authentication/sso/components/sso-link/sso-link';
import { OrDivider } from 'features/authentication/components/or-divider/or-divider';
import { useAuthenticateWithGoogle } from 'features/authentication/hooks/use-authenticate-with-google';
import { shouldRenderMobileApp } from 'utils/detect-environment';
import { PrivacyDisclaimer } from 'features/authentication/components/privacy-disclaimer/privacy-disclaimer';
import { setDocumentTitle } from 'features/authentication/utils/utils';
import styles from './signup-email-step.module.scss';

type SignupEmailStepProps = {
    userEmail: string;
    setUserEmail: (email: string) => void;
    serverError: AuthError | undefined;
    setServerError: (error: AuthError | undefined) => void;
};

type SignupEmailStepForm = {
    email: string;
};

export const SignupEmailStep = ({
    userEmail,
    setUserEmail,
    setServerError,
}: SignupEmailStepProps) => {
    useEffect(() => {
        setDocumentTitle('Signup');
    }, []);

    const urlUtmParams = {
        utm_source: useQuery('utm_source'),
        utm_medium: useQuery('utm_medium'),
        utm_campaign: useQuery('utm_campaign'),
    };
    const nextUrl = useEncodedQuery('next_url') || '';
    const { loginViaGoogle, loading } = useAuthenticateWithGoogle({
        authMode: 'signup',
        urlUtmParams,
        setServerError,
    });
    const history = useHistory();
    const location = useLocation();
    const { control, handleSubmit } = useForm<SignupEmailStepForm>({
        defaultValues: {
            email: userEmail,
        },
        mode: 'onSubmit',
    });
    const isMobile = shouldRenderMobileApp();

    const continueToSignUpForm = (inputUserEmail: string) => {
        setUserEmail(inputUserEmail);
        history.push({
            pathname: SIGNUP_FULL_FORM_STEP_ROUTE,
            search: location.search,
        });
    };

    const onSubmit = (values: SignupEmailStepForm) => {
        setServerError(undefined);
        registerHubspotLead(values.email, urlUtmParams);
        continueToSignUpForm(values.email);
    };

    const handleKeyPress = (e: KeyboardEvent) => {
        if (e.charCode === 13) {
            e.preventDefault();
            handleSubmit(onSubmit)();
        }
    };

    return (
        <div className={styles.root}>
            <div className={styles.title}>
                <Heading variant={isMobile ? 'h4' : 'h2'}>
                    Access free insights for unlimited time, No credit card needed
                </Heading>
            </div>
            <div className={styles.formWrapper}>
                <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                    <SignInGoogleButton
                        fullWidth
                        onSuccess={loginViaGoogle}
                        label="Continue with Google"
                    />
                    <OrDivider />
                    <InputFieldController<SignupEmailStepForm>
                        name="email"
                        placeholder="name@company.com"
                        control={control}
                        rules={emailRules}
                        onKeyPress={handleKeyPress}
                        disabled={loading}
                        autoFocus
                    />
                    <Button
                        testId="signup-with-email-button"
                        variant="primary-brand"
                        fullWidth
                        isLoading={loading}
                    >
                        Continue with Email
                    </Button>
                </form>
            </div>
            <div className={styles.footer}>
                <div className={styles.signInOptionsSection}>
                    <div className={styles.alreadyHaveAnAccountWrapper}>
                        <BodyText color="secondary">Already have an account?</BodyText>{' '}
                    </div>
                    <div className={styles.optionsWrapper}>
                        <Link
                            onClick={() =>
                                goToSignIn({
                                    nextUrl,
                                })
                            }
                            size="medium"
                            weight="semi-bold"
                            hideUnderline={false}
                        >
                            Login
                        </Link>{' '}
                        <BodyText color="secondary">or continue with</BodyText>{' '}
                        <SsoLink sourcePage="signup" leanVersion />
                    </div>
                </div>
                <PrivacyDisclaimer />
            </div>
        </div>
    );
};
